import supabase from '../config/supabase';
import config from '../config/environment';
import type { PortalAccountProfile } from '../types/portalUser';

interface UpdatePortalAccountParams {
  fullName: string;
  email: string;
  password?: string;
  currentPassword?: string;
}

interface PortalAccountResponse {
  success: boolean;
  message?: string;
  data?: PortalAccountProfile;
  error?: string;
}

class AccountServiceClass {
  private async getAccessToken(): Promise<string> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    if (!token) {
      throw new Error('Usuária não autenticada. Faça login novamente.');
    }

    return token;
  }

  async getCurrentAccount(): Promise<PortalAccountProfile> {
    const token = await this.getAccessToken();

    if (!config.supabaseFunctionsUrl) {
      throw new Error('Configuração do Supabase Functions não encontrada.');
    }

    const response = await fetch(`${config.supabaseFunctionsUrl}/manage-portal-account`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
    });

    const body = (await response.json().catch(() => ({}))) as Partial<PortalAccountResponse>;

    if (!response.ok || body.success === false || !body.data) {
      throw new Error(body.error || 'Não foi possível carregar os dados da conta.');
    }

    return body.data;
  }

  async updateCurrentAccount({
    fullName,
    email,
    password,
    currentPassword,
  }: UpdatePortalAccountParams): Promise<string> {
    const token = await this.getAccessToken();

    if (!config.supabaseFunctionsUrl) {
      throw new Error('Configuração do Supabase Functions não encontrada.');
    }

    const response = await fetch(`${config.supabaseFunctionsUrl}/manage-portal-account`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        full_name: fullName,
        email,
        password,
        current_password: currentPassword,
      }),
    });

    const body = (await response.json().catch(() => ({}))) as Partial<PortalAccountResponse>;

    if (!response.ok || body.success === false) {
      throw new Error(body.error || 'Não foi possível atualizar a conta.');
    }

    return body.message || 'Conta atualizada com sucesso.';
  }
}

export const AccountService = new AccountServiceClass();
